/* eslint-disable react/jsx-props-no-spreading */
import React from 'react'
import { useTranslation } from 'react-i18next'
// @ts-ignore
import { sys } from '@netbasequid/theme'
// @ts-ignore
import { Tooltip, QuestionMarkOutlined, Skeleton } from '@netbasequid/core'
import { Theme } from 'features/ui/types'
import { useDisplayTheme } from 'hooks/useUiState'
import { Status } from 'features/types'
import { setAutoId } from 'utils/automationUtil'
import {
  StyledContainer,
  StyledLeft,
  StyledTitleContainer,
  StyledInfo,
  StyledCount,
  StyledPercent,
  StyledRight,
  StyledIconBox
} from './index.css'
import TitleContainer from './TitleContainer'

type OverviewProps = {
  title: React.ReactNode
  displayTitleTooltip?: boolean
  titleTooltipContent?: string
  count?: number | string
  rightContent?: React.ReactNode
  percent?: string | undefined
  customizedInfo?: React.ReactNode
  status: Status
  testId?: string
}

const SKELETON_TITLE_WIDTH = '98px'
const SKELETON_TITLE_HEIGHT = '22px'
const SKELETON_INFO_WIDTH = '264px'
const SKELETON_INFO_HEIGHT = '28px'
const SKELETON_SPAKRLINE_WIDTH = '356px'
const SKELETON_SPAKRLINE_HEIGHT = '54px'

const getNumberFormatter = () =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3
  })

const OverviewLoading = ({
  theme,
  displayRight = true
}: {
  theme: Theme
  displayRight?: boolean
}) => (
  <StyledContainer>
    <StyledLeft>
      <Skeleton
        mode={theme}
        variation="rectangle"
        width={SKELETON_TITLE_WIDTH}
        height={SKELETON_TITLE_HEIGHT}
        animation="wave"
      />
      <Skeleton
        mode={theme}
        variation="rectangle"
        width={SKELETON_INFO_WIDTH}
        height={SKELETON_INFO_HEIGHT}
        animation="wave"
      />
    </StyledLeft>
    {displayRight && (
      <Skeleton
        mode={theme}
        variation="rectangle"
        width={SKELETON_SPAKRLINE_WIDTH}
        height={SKELETON_SPAKRLINE_HEIGHT}
        animation="wave"
      />
    )}
  </StyledContainer>
)

export const PercentDisplay = ({
  count,
  sideLabel
}: {
  count: OverviewProps['count']
  sideLabel: React.ReactNode
}) => {
  const theme = useDisplayTheme() as Theme
  return (
    <>
      <StyledCount>
        {typeof count === 'number' ? getNumberFormatter().format(count) : count}
      </StyledCount>
      {sideLabel && <StyledPercent mode={theme}>&nbsp;{sideLabel}</StyledPercent>}
    </>
  )
}

const Overview = ({
  title,
  displayTitleTooltip = false,
  titleTooltipContent,
  count,
  rightContent = null,
  percent,
  customizedInfo,
  status,
  testId
}: OverviewProps) => {
  const theme = useDisplayTheme() as Theme
  const { t } = useTranslation()

  return (
    <>
      {status === 'loading' ? (
        <OverviewLoading theme={theme} displayRight={!!rightContent} />
      ) : (
        <StyledContainer {...(testId && setAutoId(testId))}>
          <StyledLeft>
            <StyledTitleContainer>
              <TitleContainer>{title}</TitleContainer>
              <StyledIconBox mode={theme}>
                {displayTitleTooltip && (
                  <Tooltip mode={theme} content={titleTooltipContent}>
                    <QuestionMarkOutlined color={sys[theme].text.placeholder} />
                  </Tooltip>
                )}
              </StyledIconBox>
            </StyledTitleContainer>
            <StyledInfo>
              {customizedInfo || (
                <PercentDisplay
                  count={count}
                  sideLabel={
                    percent !== undefined &&
                    t('percentOfTotal', { percent, interpolation: { escapeValue: false } })
                  }
                />
              )}
            </StyledInfo>
          </StyledLeft>
          <StyledRight>{rightContent}</StyledRight>
        </StyledContainer>
      )}
    </>
  )
}

export default Overview
